import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationByInternalUser,
} from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectedUserWithLoanChannel,
	selectedUserWithAccessLevel,
} from "../../utils/common-helpers";

import { useQuickPricerForm } from "./hooks/useQuickPricerForm";
import { useQuickPricerApis } from "./hooks/useQuickPricerApis";
import { buildQuickPricerPayload } from "./utils/buildQuickPricerPayload";
import QuickPricerForm from "./components/QuickPricerForm";
import QuickPricerModals from "./components/QuickPricerModals";
import QuickPricerListView from "./components/QuickPricerListView";
import {
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
	BROKER_ADMIN,
	TPO_SECONDARY_USER_TITLE,
} from "../../utils/common-constants";
import NavigationModal from "../../components/modal/navigation-modal";
import PrimaryButton from "../../components/buttons/primary-button";
import ProcessingModal from "../../components/modal/processing-modal";
import QuickPricerPrintPreview from "../quick-pricer-print";
const NewQuickPricer = ({ isPublic = false }) => {
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const selectedOrganizationByInternalUser = useSelector(selectOrganizationByInternalUser);
	const currentUser = selectedUserWithLoanChannel(
		userDetails,
		impersonatedUser,
		selectedOrganization
	);
	//  NEW STATE (controls form vs results view)
	const [showResults, setShowResults] = useState(false);

	// LC-324: Print to PDF - programs selected in QuickPricerListView (that
	// view owns the selection since it owns products/productDetails), the
	// in-flight hydration state while missing productDetails are fetched, and
	// the print preview itself.
	const [selectedProgramIds, setSelectedProgramIds] = useState([]);
	// Keyed by `${productId}_${quoteIndex}` - which individual rate rows are
	// checked in "Show All Options", so the PDF can highlight the same rows.
	const [selectedRateKeys, setSelectedRateKeys] = useState([]);
	const [isPreparingPrint, setIsPreparingPrint] = useState(false);
	const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
	const [printGeneratedAt, setPrintGeneratedAt] = useState(null);
	const isExecutiveOrManager =
			userDetails?.user_roles?.includes("admin") ||
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant");
	const {
		formState,
		setFormState,
		errors,
		setErrors,
		submitted,
		setSubmitted,
		validateForm,
		sections,
	} = useQuickPricerForm();

	const {
		fetchCounty,
		runQuickPricerFlow,
		handleProductClick,
		products,
		ineligibleProducts,
		loading,
		commonErrorModalVisible,
		commonErrorMessage,
		commonErrorCode,
		commonErrorTitle,
		handleCloseCommonErrorModal,
	} = useQuickPricerApis(currentUser);

	const navigate = useNavigate();
	const prevSelectedOrg = useRef();
	const pendingOrgSwitchCheck = useRef(false);
	const [navigationModalText, setNavigationModalText] = useState("");
	const [navigationModalVisible, setNavigationModalVisible] = useState(false);

	// Resolves the effective loan channel for CORR/NDC gating on this page.
	// Prefers selectedUserWithLoanChannel (impersonation + array/string aware),
	// but that helper matches organizations[] by exact name against
	// selectedOrganization - which is only ever set by OrganizationDropdown's
	// own effect, and that dropdown doesn't always run for external single-org
	// users on this page. When the name match comes up empty, fall back to the
	// user's only organization (single-org accounts) and then to
	// snowFlakeOrganizations (different key names: company_name, and
	// loan_channel isn't always populated there either).
	const resolveEffectiveLoanChannel = () => {
		if (currentUser?.loan_channel) return currentUser.loan_channel;

		const organizations = userDetails?.organizations;
		if (Array.isArray(organizations) && organizations.length > 0) {
			const matchedByName = organizations.find(
				(org) => org.name === selectedOrganization
			);
			if (matchedByName?.loan_channel) return matchedByName.loan_channel;
			if (organizations.length === 1 && organizations[0]?.loan_channel) {
				return organizations[0].loan_channel;
			}
		}

		const snowFlakeOrganizations = userDetails?.snowFlakeOrganizations;
		if (Array.isArray(snowFlakeOrganizations) && snowFlakeOrganizations.length > 0) {
			const matchedByCompanyName = snowFlakeOrganizations.find(
				(org) => org.company_name === selectedOrganization
			);
			if (matchedByCompanyName?.loan_channel) return matchedByCompanyName.loan_channel;
			if (snowFlakeOrganizations.length === 1 && snowFlakeOrganizations[0]?.loan_channel) {
				return snowFlakeOrganizations[0].loan_channel;
			}
		}

		return undefined;
	};

	const isCorrOrNdcChannel = () => {
		const loanChannel = resolveEffectiveLoanChannel();

		console.log("Loan Channel:", loanChannel);

		return (
			loanChannel === LOAN_CHANNEL_CONSTANTS.CORR ||
			loanChannel === LOAN_CHANNEL_CONSTANTS.NDC
		);
	};

	// CORR/NDC users with TPO Secondary or Broker Admin access still have Quick
	// Pricer enabled (see custom-drawer/index.jsx's isItemDisabled) - the
	// org-switch popup below needs to agree with that instead of treating every
	// CORR/NDC org as unavailable.
	const isTpoSecondaryOrBrokerAdmin = () => {
		const effectiveUserForAccess = selectedUserWithAccessLevel(userDetails, impersonatedUser);
		const accessLevel = effectiveUserForAccess?.access_level;
		const userTitle = effectiveUserForAccess?.user_title;
		return (
			accessLevel === TPO_SECONDARY ||
			accessLevel === BROKER_ADMIN ||
			userTitle?.includes(TPO_SECONDARY_USER_TITLE)
		);
	};

	// Lender Paid (LPC) vs. Borrower Paid (BPC) is disabled on the form for CORR/NDC
	// channels (see QuickPricerForm.jsx); default it to Lender Paid there so the
	// (now non-required) field still carries a valid value into the payload. Every
	// other channel's flow is untouched.
	useEffect(() => {
		if (isCorrOrNdcChannel() && !formState.lenderPaidvsBorrowerPaid) {
			setFormState((prev) => ({ ...prev, lenderPaidvsBorrowerPaid: "YesLenderPaid" }));
		}
	}, [userDetails, impersonatedUser, selectedOrganization]);
	const handleOpenNavigationModal = () => {
		setNavigationModalVisible(true);
	};

	const handleCloseNavigationModal = () => {
		setNavigationModalVisible(false);
	};

	const handleNavigateHome = (e) => {
		handleCloseNavigationModal();
		
		if (isExecutiveOrManager) {
			handleSubmit(e);
		} else if (isCorrOrNdcChannel() && !isTpoSecondaryOrBrokerAdmin()) {
			navigate("/home");
			return;
		} else {
			handleSubmit(e); // Trigger form submission to refresh data based on new organization selection
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setSubmitted(true);

		const validationErrors = validateForm();
		setErrors(validationErrors);
		if (Object.keys(validationErrors).length > 0) return;

		const requestBody = buildQuickPricerPayload(formState);

		//  SHOW RESULTS VIEW
		setShowResults(true);

		runQuickPricerFlow(requestBody);
	};

	//  Back to Form Handler
	const handleBackToForm = () => {
		setShowResults(false);
		setIsPreparingPrint(false);
		setPrintPreviewOpen(false);
	};

	const handleClosePrintPreview = () => {
		setPrintPreviewOpen(false);
	};

	const handlePrintToPdf = () => {
		const missingProductIds = selectedProgramIds.filter((productId) => {
			const product = products.find((p) => p.productId === productId);
			return !product?.productDetails;
		});

		if (missingProductIds.length === 0) {
			setPrintGeneratedAt(new Date());
			setPrintPreviewOpen(true);
			return;
		}

		setIsPreparingPrint(true);
		missingProductIds.forEach((productId) => handleProductClick(productId));
	};

	// Once every selected program has its rate stack/adjustments hydrated
	// (fetched lazily, same as "Show All Options" does), open the preview.
	useEffect(() => {
		if (!isPreparingPrint) return;

		const allSelectedProductsReady = selectedProgramIds.every((productId) => {
			const product = products.find((p) => p.productId === productId);
			return Boolean(product?.productDetails);
		});

		if (allSelectedProductsReady) {
			setIsPreparingPrint(false);
			setPrintGeneratedAt(new Date());
			setPrintPreviewOpen(true);
		}
	}, [products, isPreparingPrint, selectedProgramIds]);

	// If hydrating a selected program fails, the common error modal already
	// surfaces it (handleProductClick's own catch) - just abort the print flow.
	useEffect(() => {
		if (commonErrorModalVisible) {
			setIsPreparingPrint(false);
		}
	}, [commonErrorModalVisible]);

	const selectedProductsForPrint = selectedProgramIds
		.map((productId) => products.find((p) => p.productId === productId))
		.filter(Boolean);

	const orgLabel = isExecutiveOrManager
		? selectedOrganizationByInternalUser
		: selectedOrganization;
	/** Quicker Pricer On Change Organization Condition */
	const quickPricerOnChangeOrganization = () => {

		if (isExecutiveOrManager) {
			setNavigationModalText(
				`Quick Pricer is available for ${selectedOrganizationByInternalUser || ""}. Please refresh the page to continue.`
			);
			handleOpenNavigationModal();
		} else if (!isCorrOrNdcChannel() || isTpoSecondaryOrBrokerAdmin()) {
			setNavigationModalText(
				`Quick Pricer is available for ${selectedOrganization || ""}. Please refresh the page to continue.`
			);
			handleOpenNavigationModal();
		} else {
			setNavigationModalText(
				`Quick Pricer is not available for ${selectedOrganization || ""}. Please contact your Account Executive for assistance.`
			);
			handleOpenNavigationModal();
		}
		
	};

	useEffect(() => {
		if (formState.zipcode?.length === 5) {
			fetchCounty(formState.zipcode, setFormState);
		}
	}, [formState.zipcode]);

	// Executive/manager path: unchanged from before - quickPricerOnChangeOrganization()
	// only reads selectedOrganizationByInternalUser (already fresh at the moment this
	// fires), never access_level/channel, so it's safe to run immediately here.
	useEffect(() => {
		if (isExecutiveOrManager) {
			if (
				prevSelectedOrg.current &&
				prevSelectedOrg.current !== selectedOrganizationByInternalUser &&
				userDetails
			) {
				quickPricerOnChangeOrganization();
			}
			prevSelectedOrg.current = selectedOrganizationByInternalUser;
		} else {
			// Non-executive path: only *detect* the switch here. RequireAuth.js
			// refetches userDetails (scoped to the new org via the TPO-ORG-ID header)
			// asynchronously whenever selectedOrganization changes - isTpoSecondaryOrBrokerAdmin()
			// and isCorrOrNdcChannel() both need that refreshed, org-scoped userDetails.
			// Firing quickPricerOnChangeOrganization() here would still see the
			// previous org's stale access_level, then mark the org "handled" before
			// the fresh data ever arrives. Defer to the effect below instead, which
			// waits for that refetch to actually land.
			if (prevSelectedOrg.current && prevSelectedOrg.current !== selectedOrganization) {
				pendingOrgSwitchCheck.current = true;
			}
			prevSelectedOrg.current = selectedOrganization;
		}
	}, [selectedOrganization, selectedOrganizationByInternalUser]);

	useEffect(() => {
		if (pendingOrgSwitchCheck.current && userDetails) {
			pendingOrgSwitchCheck.current = false;
			quickPricerOnChangeOrganization();
		}
	}, [userDetails]);

	return (
		<>
			{/* ================= FORM VIEW ================= */}
			{!showResults && (
				<>
					<QuickPricerForm
						sections={sections}
						formState={formState}
						setFormState={setFormState}
						errors={errors}
						setErrors={setErrors}
						submitted={submitted}
						handleSubmit={handleSubmit}
						isCorrOrNdcChannel={isCorrOrNdcChannel()}
					/>
				</>
			)}

			{/* ================= LIST VIEW ================= */}
			{showResults && (
				<div style={{ width: "100%" }}>
					<div className="update-loan-scenario-bar">
						<div className="scenario-header">
							<div className="scenario-title">
								<button className="update-btn" onClick={handleBackToForm}>
									Update Loan Scenario
								</button>

								<span className="edit-data" onClick={handleBackToForm}>
									✎ Edit Data
								</span>

								<PrimaryButton
									text="Print to PDF"
									onClick={handlePrintToPdf}
									disabled={selectedProgramIds.length === 0}
								/>
							</div>
						</div>
					</div>

					<QuickPricerListView
						products={products}
						ineligibleProducts={ineligibleProducts}
						handleProductClick={handleProductClick}
						runQuickPricerFlow={runQuickPricerFlow}
						onPrintSelectionChange={setSelectedProgramIds}
						onRateSelectionChange={setSelectedRateKeys}
						isCorrOrNdcChannel={isCorrOrNdcChannel()}
					/>
				</div>
			)}

			<QuickPricerModals
				loading={loading}
				commonErrorModalVisible={commonErrorModalVisible}
				commonErrorMessage={commonErrorMessage}
				commonErrorCode={commonErrorCode}
				commonErrorTitle={commonErrorTitle}
				handleCloseCommonErrorModal={handleCloseCommonErrorModal}
			/>
			<NavigationModal
				open={navigationModalVisible}
				text={navigationModalText}
				confirmText={isCorrOrNdcChannel() || !isExecutiveOrManager ? "Okay" : "Refresh Search"}
				handleConfirm={handleNavigateHome}
			/>
			<ProcessingModal
				open={isPreparingPrint}
				text="Preparing your PDF..."
			/>
			<QuickPricerPrintPreview
				open={printPreviewOpen}
				handleClose={handleClosePrintPreview}
				formState={formState}
				selectedProducts={selectedProductsForPrint}
				selectedRateKeys={selectedRateKeys}
				currentUser={currentUser}
				orgLabel={orgLabel}
				generatedAt={printGeneratedAt}
				isCorrOrNdcChannel={isCorrOrNdcChannel()}
				isPublic={isPublic}
			/>
		</>
	);
};

export default NewQuickPricer;
